// ============================================
// KeyboardShortcutsOverlay — Shows available
// keyboard shortcuts when user presses "?"
// ============================================

import { useEffect, useState } from 'react';
import { NavLink } from 'react-router';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Keyboard, Mic, Navigation, Search, BookOpen, ArrowRight } from 'lucide-react';
import { cn } from './ui/utils';

interface Shortcut {
  key: string;
  description: string;
  category: string;
  icon?: React.ReactNode;
  secondaryKey?: string; // For combined keys like "Hold Space"
}

const shortcuts: Shortcut[] = [
  {
    key: 'Space',
    secondaryKey: 'Hold',
    description: 'Activate voice commands (desktop only)',
    category: 'Voice Commands',
    icon: <Mic className="w-4 h-4 text-indigo-400" />,
  },
  {
    key: '?',
    description: 'Show this keyboard shortcuts overlay',
    category: 'General',
    icon: <Keyboard className="w-4 h-4 text-blue-400" />,
  },
  {
    key: 'K',
    secondaryKey: '⌘ / Ctrl +',
    description: 'Search help articles and jump to any page',
    category: 'General',
    icon: <Search className="w-4 h-4 text-indigo-400" />,
  },
];

export function KeyboardShortcutsOverlay() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const isEditableTarget = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if ((e.target as HTMLElement)?.isContentEditable) return true;
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if the "?" key is pressed (Shift + / on US keyboards)
      if (e.key === '?' && !isEditableTarget(e)) {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      // Also support Escape to close
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  // Group shortcuts by category
  const categories = shortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [];
    }
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {} as Record<string, Shortcut[]>);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-400/30">
              <Keyboard className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <DialogTitle>Keyboard Shortcuts</DialogTitle>
              <DialogDescription>
                Speed up your workflow with these keyboard shortcuts
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {Object.entries(categories).map(([category, categoryShortcuts]) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                {category}
              </h3>
              <div className="space-y-2">
                {categoryShortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {shortcut.icon && (
                        <div className="flex items-center justify-center w-8 h-8 rounded-md bg-background">
                          {shortcut.icon}
                        </div>
                      )}
                      <span className="text-sm text-foreground">
                        {shortcut.description}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {shortcut.secondaryKey && (
                        <span className={cn(
                          "px-2 py-1 rounded-md border border-border bg-background/50",
                          "text-xs font-sans text-muted-foreground"
                        )}>
                          {shortcut.secondaryKey}
                        </span>
                      )}
                      <kbd className={cn(
                        "px-3 py-1.5 rounded-md border border-border bg-background",
                        "text-xs font-mono font-semibold tracking-wider",
                        "shadow-sm"
                      )}>
                        {shortcut.key}
                      </kbd>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t border-border space-y-3">
          <NavLink
            to="/help"
            onClick={() => setOpen(false)}
            className="flex items-center justify-between rounded-lg border border-indigo-200 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-800 transition-colors hover:bg-indigo-50"
          >
            <span className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              View the full Help &amp; Wiki
            </span>
            <ArrowRight className="h-4 w-4" />
          </NavLink>
          <p className="text-xs text-muted-foreground text-center">
            Press <kbd className="px-2 py-0.5 rounded border border-border bg-muted text-[10px] font-mono">?</kbd> or <kbd className="px-2 py-0.5 rounded border border-border bg-muted text-[10px] font-mono">Esc</kbd> to close this dialog
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}